import { Drawer, Button, Typography, Box, IconButton, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OperationForm } from './OperationForm';
import { useEffect, useState } from 'react';
import { PatchType } from '../../../types';
// import { useDispatch } from 'react-redux';

import { useMenuDataCache } from '../hooks/useMenuDataCache';
import { useOperationForm } from '../hooks/useOperationForm';

interface PatchOperation {
  __typename: string;
  id?: string;
  patchOperationId?: string;
  affectedStores?: string[];
  storeCount?: number;
  originalIds?: string[];
  bundle?: { name?: string; internalName?: string; code?: string; bundleCode?: string; bundleId?: string; id?: string };
  product?: { name?: string; internalName?: string; code?: string; productCode?: string; productId?: string; id?: string };
  variant?: { name?: string; internalName?: string; code?: string; variantCode?: string; variantId?: string; id?: string };
  modifier?: { name?: string; internalName?: string; code?: string; modifierCode?: string; modifierId?: string; id?: string };
  category?: { name?: string; internalName?: string; code?: string; categoryCode?: string; categoryId?: string; id?: string };
  choice?: { name?: string; internalName?: string; code?: string; choiceCode?: string; choiceId?: string; id?: string };
  optionValue?: { name?: string; internalName?: string; code?: string; optionValueCode?: string; optionValueId?: string; id?: string };
  modifierWeight?: { name?: string; internalName?: string; code?: string; modifierWeightCode?: string; modifierWeightId?: string; weightId?: string; id?: string };
  slot?: { name?: string; internalName?: string; code?: string; slotCode?: string; slotId?: string; id?: string };
  // Legacy operation properties
  setBundlePrice?: { bundle?: { name?: string; internalName?: string; bundleCode?: string; bundleId?: string } };
  setVariantPrice?: { variant?: { name?: string; internalName?: string; variantCode?: string; variantId?: string } };
}

interface Patch {
  patchId?: string;
  id?: string;
  name?: string;
  storeNumber: string; // Make this required since we need it for operations
  operations?: PatchOperation[];
}

interface OperationDrawerProps {
  mode?: 'create' | 'edit' | 'remove';
  initialEdit?: any;
  onClose?: () => void;
  patchType?: PatchType;
  availablePatches?: Patch[];
  selectedStores?: any[];
  getPatchIdsForType?: (patchType: PatchType) => string[];
  addPendingOp?: (opObj: { minimal: any; enriched: any }) => void;
}

export function OperationDrawer({ mode = 'create', initialEdit = null, onClose, patchType = PatchType.PRODUCT_PRICE, availablePatches = [], selectedStores = [], getPatchIdsForType, addPendingOp }: OperationDrawerProps) {
  // Determine mode flags early
  const isEdit = mode === 'edit';
  const isRemove = mode === 'remove';

  // Static mapping of allowed operation types per patch type
  const operationTypesByPatchType: Record<PatchType, string[]> = {
    [PatchType.PRODUCT_PRICE]: [
      'SetVariantPriceMenuPatchOperation',
      'SetModifierWeightPriceMenuPatchOperation',
      'SetModifierPriceRangesMenuPatchOperation',
    ],
    [PatchType.BUNDLE_PRICE]: [
      'SetBundlePriceMenuPatchOperation',
    ],
    [PatchType.PRODUCT_EXCLUSION]: [
      'RemoveProductMenuPatchOperation',
      'RemoveVariantMenuPatchOperation',
      'RemoveModifierFromSlotPatchOperation',
      'RemoveModifierMenuPatchOperation',
      'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
    ],
    [PatchType.BUNDLE_EXCLUSION]: [
      'RemoveBundleMenuPatchOperation',
    ]
  };

  // Use static mapping for operation types
  const uniqueOperationTypes = operationTypesByPatchType[patchType] || [];
  // console.log('[OperationDrawer] patchType:', patchType, 'uniqueOperationTypes:', uniqueOperationTypes, 'availablePatches:', availablePatches);

  // Operation type selection
  const [operationType, setOperationType] = useState<string>(uniqueOperationTypes[0] || '');

  // Menu data cache
  const { menu, fetchMenuData } = useMenuDataCache();

  // Operation form state
  const {
    fields,
    handlers
  } = useOperationForm({
    mode,
    operationTypes: uniqueOperationTypes,
    availablePatches,
    addPendingOp,
    menu, // <-- Pass menu data to the hook
  });

  // On mount, fetch menu data (will only load once due to cache logic)
  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // When patch type changes, update operation type to first available
  useEffect(() => {
    if (uniqueOperationTypes.length > 0) {
      setOperationType(uniqueOperationTypes[0]);
      handlers.setOperationType(uniqueOperationTypes[0]);
    }
  }, [patchType]);

  // When operation type changes, update form state
  useEffect(() => {
    if (operationType) {
      handlers.setOperationType(operationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationType]);

  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const handleSave = () => {
    // Always enabled, just call the form's save handler
    handlers.save();
    handleClose();
  };

  return (
    <>
      {isEdit && (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          color="primary"
        >
          Edit Operation
        </Button>
      )}

      <Drawer anchor="right" open={open || isEdit} onClose={handleClose}>
        <Box p={2} width={600} data-testid="operationWindow">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              {isEdit ? 'Edit Operation' : isRemove ? 'Remove Operation' : 'Add Operation'}
            </Typography>
            <IconButton onClick={handleClose} data-testid="closeOperationDrawerButton">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Patch selection dropdown removed: operations are based on patch type only */}

          {/* Operation type select */}
          {uniqueOperationTypes.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="operation-type-select-label">Operation Type</InputLabel>
              <Select
                labelId="operation-type-select-label"
                data-testid="operation-type-select"
                value={operationType}
                label="Operation Type"
                onChange={(e) => setOperationType(e.target.value)}
              >
                {uniqueOperationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace('MenuPatchOperation', '').replace(/([A-Z])/g, ' $1').trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Operation form */}
          {operationType && (
            <Box mt={2}>
              <OperationForm
                fields={fields}
                handlers={handlers}
                mode={mode}
              />
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              data-testid="cancelOperationButton"
              onClick={handleClose}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              data-testid="saveOperationButton"
              onClick={handleSave}
              color={isRemove ? "error" : "primary"}
            >
              {isEdit ? 'Update' : isRemove ? 'Remove' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
